import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    Modal,
    Form,
    Select,
    Input,
    message,
    Row,
    Col, Divider
} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import {LeftOutlined} from "@ant-design/icons";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Title} = Typography;
const {Option} = Select;

const CCTripDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [trip, setTrip] = useState<any>(null);
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        fetchTrip();
    }, [id]);

    const fetchTrip = async () => {
        try {
            const res = await api.get(`/cc/trips/${id}/details/`);

            const data = res.data;
            setTrip(data);
            form.setFieldsValue({
                buy_in_method: data.trip?.buy_in_method,
                buyin_amount: data.trip?.buyin_amount,
                buyin_currency: data.trip?.buyin_currency,
                additional_expenses: data.trip?.additional_expenses,
                trip_notes: data.trip?.trip_notes,
            });
        } catch (err) {
            console.error(err);
            message.error("Failed to load trip details.");
        }
    };

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                buy_in_method: values.buy_in_method || null,
                buyin_amount: values.buyin_amount ? Number(values.buyin_amount.toString().replace(/,/g, "")) : null,
                buyin_currency: values.buyin_currency || null,
                additional_expenses: values.additional_expenses ? Number(values.additional_expenses.toString().replace(/,/g, "")) : 0,
                trip_notes: values.trip_notes || "",
            };
            await api.patch(`/cc/trips/${id}/details/`, payload);
            message.success("Trip updated successfully.");
            setModalVisible(false);
            fetchTrip();
        } catch (error) {
            console.error("Validation or update failed:", error);
            message.error("Failed to update trip.");
        }
    };

    const formatCurrency = (
        amount: number | string | null | undefined,
        currency?: string | null
    ) => {
        if (amount == null) return "-";
        const n =
            typeof amount === "string"
                ? Number(amount.replace(/,/g, ""))
                : Number(amount);
        if (Number.isNaN(n)) return "-";

        const symbolMap: Record<string, string> = {
            KRW: "₩",
            Yen: "¥",
            HKD: "HK$",
            NTD: "NT$",
            USDT: "US$",
        };
        const symbol = currency ? (symbolMap[currency] ?? "") : "";

        // show amount even if currency is missing; just omit the symbol
        return `${symbol}${n.toLocaleString()}`;
    };

    if (!trip) return null;

    const toNum = (v: unknown) => (v == null || v === '' ? 0 : Number(v));
    const flightCost = toNum(trip?.flight?.total_flight_cost_krw);
    const addl = toNum(trip?.trip?.additional_expenses);
    const totalKRW = flightCost + addl;

    return (
        <div style={{padding: 24}}>
            <Button
                icon={<LeftOutlined/>}
                onClick={() => navigate(-1)}
                style={{marginBottom: 24}}
                className="rounded-md"
            >
                Back
            </Button>

            <Title level={3} style={{marginBottom: 16}}>
                Trip Details
            </Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{margin: 0}}>
                                Casino Club Trip Overview
                            </Title>
                        </Col>
                    </Row>
                }
                extra={
                    <Button type="primary" onClick={() => setModalVisible(true)} className="rounded-md">
                        Edit
                    </Button>
                }
            >
                <Descriptions bordered column={1} style={maxW700}>
                    <Descriptions.Item label="Customer Name" labelStyle={boldLabelStyle}>
                        <NoWrap>{trip.customer?.first_name} {trip.customer?.last_name}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="MMID" labelStyle={boldLabelStyle}>
                        <NoWrap>{trip.customer?.mmid}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Check-in Date" labelStyle={boldLabelStyle}>
                        {trip.hotel?.checkin_date || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Check-out Date" labelStyle={boldLabelStyle}>
                        {trip.hotel?.checkout_date || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Flight Number" labelStyle={boldLabelStyle}>
                        {trip.flight?.in_flight_number || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Arrival Airport" labelStyle={boldLabelStyle}>
                        {trip.flight?.in_arrival_airport || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="IN Arrival Time" labelStyle={boldLabelStyle}>
                        {trip.flight?.in_arrival_time
                            ? dayjs(trip.flight.in_arrival_time, ["HH:mm:ss", "HH:mm"]).format("HH:mm")
                            : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Buy-in Method" labelStyle={boldLabelStyle}>
                        {trip.trip?.buy_in_method || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Buy-in Amount" labelStyle={boldLabelStyle}>
                        <NoWrap>{formatCurrency(trip.trip?.buyin_amount, trip.trip?.buyin_currency)}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Buy-in Currency" labelStyle={boldLabelStyle}>
                        <NoWrap>{trip.trip?.buyin_currency || "-"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Additional Expenses (KRW)" labelStyle={boldLabelStyle}>
                        <NoWrap>₩{addl.toLocaleString()}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Total Flight Cost (KRW)" labelStyle={boldLabelStyle}>
                        <NoWrap>₩{flightCost.toLocaleString()}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Total Trip Expenses" labelStyle={boldLabelStyle}>
                        <NoWrap>₩{totalKRW.toLocaleString()}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Trip Notes" labelStyle={boldLabelStyle}>
                        <NoWrap>{trip.trip?.trip_notes || "-"}</NoWrap>
                    </Descriptions.Item>
                </Descriptions>

                <Modal
                    title={<span className="text-base font-semibold">Edit Trip Info</span>}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    onOk={handleUpdate}
                    centered
                    width={600}
                    okButtonProps={{className: "rounded-md"}}
                    cancelButtonProps={{className: "rounded-md"}}
                >
                    <Divider className="my-2"/>
                    <Form layout="vertical" form={form}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="buy_in_method" label="Buy-in Method">
                                    <Select allowClear placeholder="Select method">
                                        <Option value="Cash">Cash</Option>
                                        <Option value="Crypto">Crypto</Option>
                                        <Option value="Credit Card">Credit Card</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="buyin_currency" label="Buy-in Currency">
                                    <Select allowClear placeholder="Currency">
                                        <Option value="Yen">Yen</Option>
                                        <Option value="KRW">KRW</Option>
                                        <Option value="HKD">HKD</Option>
                                        <Option value="NTD">NTD</Option>
                                        <Option value="USDT">USDT</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="buyin_amount" label="Buy-in Amount">
                                    <Input
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(/,/g, "");
                                            if (!isNaN(Number(raw))) {
                                                form.setFieldsValue({
                                                    buyin_amount: Number(raw).toLocaleString(),
                                                });
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="additional_expenses" label="Additional Expenses (KRW)">
                                    <Input
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(/,/g, "");
                                            if (!isNaN(Number(raw))) {
                                                form.setFieldsValue({
                                                    additional_expenses: Number(raw).toLocaleString(),
                                                });
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name="trip_notes" label="Trip Notes">
                                    <Input.TextArea rows={3}/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Card>
        </div>
    );
};

export default CCTripDetailPage;
